import { useState, useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { useAccountCreationContext } from "@/components/signup/account-creation";
import { emailIsValid, fontSize } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";

import { InlineInput, InlineSeparator, InlineSubmit, SocialButton } from "./";

export const InlineInputSubmitSocial = ({
    helpText
}: {
    helpText?: string;
}) => {
    const intl = useIntl();
    const { isReadyForInvalid, setIsEmailValid, typeAheadEmail } =
        useAccountCreationContext();
    const [email, setEmail] = useState(typeAheadEmail);
    const [isValid, setValid] = useState<boolean>();

    const defaultHelpText = intl.formatMessage({
        id: "signupForm.accountCreation.helperTextTeam26",
        defaultMessage:
            "Use a work email to find teammates and get access to Rovo AI"
    });
    const emailHelpText = helpText?.trim() || defaultHelpText;

    const errorMessageText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.invalidEmail",
        defaultMessage: "Please enter a valid email address."
    });

    const placeholderText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.workEmail",
        defaultMessage: "Work email"
    });

    useEffect(() => {
        const valid = emailIsValid(email);

        if (!valid && !isReadyForInvalid) {
            setValid(undefined);
            return;
        }

        setValid(valid);
        setIsEmailValid(valid);
    }, [email, setIsEmailValid, isReadyForInvalid]);

    return (
        <div css={containerCss}>
            <label css={[labelCss, helpTextContainerCss]}>
                {placeholderText}
            </label>
            <div css={inputSubmitSocialContainerCss}>
                <div
                    css={[
                        inputSubmitGroupCss,
                        isValid === undefined && inputSubmitGroupFocusCss,
                        isValid === false && inlineInputInvalidCss,
                        isValid === true && inputValidCss
                    ]}
                >
                    <div css={inputCss}>
                        <InlineInput
                            email={email}
                            setEmail={setEmail}
                            isValid={isValid}
                            setValid={setValid}
                            errorMessageText={errorMessageText}
                        />
                        <div css={[helpTextContainerCss, helpTextMobileCss]}>
                            <label
                                css={[helpTextCss, helpErrorTextMobileCss]}
                                htmlFor="inline_email_embedded_signup_input"
                            >
                                {emailHelpText}
                            </label>
                            {isValid === false ? (
                                <div
                                    css={[
                                        helpErrorTextMobileCss,
                                        errorMessageCss
                                    ]}
                                    role="alert"
                                >
                                    {errorMessageText}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <InlineSubmit />
                </div>
                <InlineSeparator />
                <SocialButton />
            </div>
            <div css={helpTextContainerCss}>
                <label
                    css={[helpTextCss, helpErrorTextDesktopCss]}
                    htmlFor="inline_email_embedded_signup_input"
                >
                    {emailHelpText}
                </label>
                {isValid === false ? (
                    <div
                        css={[helpErrorTextDesktopCss, errorMessageCss]}
                        role="alert"
                    >
                        {errorMessageText}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const containerCss = css({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px"
});

const labelCss = css({
    color: "inherit",
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 300,
    fontSize: "14px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- hide asterisk
    ["[aria-hidden=true]"]: {
        display: "none"
    }
});

const inputSubmitSocialContainerCss = css({
    alignItems: "center",
    display: "flex",
    gap: token("space.200"),
    width: "100%",
    [media.below.sm]: {
        flexDirection: "column",
        gap: token("space.150")
    }
});

const inputSubmitGroupCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200"),
    width: "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["button"]: {
        minHeight: "auto !important"
    },
    [media.above.sm]: {
        height: "48px",
        boxSizing: "border-box",
        alignItems: "center",
        backgroundColor: wacToken("color.neutral.light0"),
        border: `1px solid ${wacToken("color.neutral.light400")}`,
        borderRadius: 40,
        flexDirection: "row",
        gap: token("space.150")
    }
});

const inputSubmitGroupFocusCss = css({
    [media.above.sm]: {
        ["&:focus-within"]: {
            boxShadow: `inset 0 0 0 1px ${wacToken("color.extended.blue600")}`,
            borderColor: wacToken("color.extended.blue600")
        }
    }
});

const inputCss = css({
    flexGrow: 1,
    width: "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["label"]: {
        margin: "0 auto"
    }
});

const helpTextContainerCss = css({
    marginInlineStart: "24px",
    marginInlineEnd: "24px"
});

const helpTextMobileCss = css({
    [media.below.sm]: {
        marginBlockStart: "4px"
    }
});

const helpTextCss = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "12px",
    lineHeight: 1.5,
    marginBlockStart: 6,
    opacity: 0.8
});

const helpErrorTextMobileCss = css({
    [media.above.sm]: {
        display: "none"
    }
});

const helpErrorTextDesktopCss = css({
    [media.below.sm]: {
        display: "none"
    }
});

const errorMessageCss = css({
    color: wacToken("color.secondary.red"),
    fontSize: fontSize.xs,
    lineHeight: 1.5
});

const inlineInputInvalidCss = css({
    [media.above.sm]: {
        borderColor: wacToken("color.secondary.red"),
        boxShadow: `inset 0 0 0 1px ${wacToken("color.secondary.red")}`
    }
});

const inputValidCss = css({
    [media.above.sm]: {
        borderColor: wacToken("color.extended.lime600"),
        boxShadow: `inset 0 0 0 1px ${wacToken("color.extended.lime600")}`
    }
});
